// frontend/src/utils/bitrixStatusMapping.js

/**
 * Улучшенная система маппинга статусов Битрикса
 * Использует данные из схемы коллекции для точного отображения
 */

// Резервные статусы лидов (если нет данных в схеме)
const FALLBACK_LEAD_STATUS_MAPPING = {
    'NEW': 'Не обработан',
    'IN_PROCESS': 'Дозвониться до клиента',
    'PROCESSED': 'Обработан',
    'QUALIFIED': 'Квалифицирован',
    'CONVERTED': 'Первая отгрузка',
    'JUNK': 'Нет связи',
    '3': 'Переговоры',
    '7': 'Нецелевой лид',
    '8': 'Отложенный спрос',
    '11': 'Выбрали не нашу компанию',
    '14': 'КП отправлено',
    '15': 'Отсутствуют контакты',
    '17': 'КП на согласование с руководством',
    '18': 'Задвоеный',
    '19': 'Закрылись',
    'UC_0VBS2N': 'Работает через дистрибьютора',
    'UC_LHZTAP': 'Сбор информации',
    'UC_5MM1GH': 'Действующий клиент',
    'UC_8LUXZC': 'База для обзвона',
}

// Резервные источники лидов (если нет данных в схеме)
const FALLBACK_LEAD_SOURCE_MAPPING = {
    'CALL': 'Звонок',
    'EMAIL': 'Электронная почта',
    'WEB': 'Веб-сайт',
    'ADVERTISING': 'Реклама',
    'PARTNER': 'Существующий клиент',
    'RECOMMENDATION': 'По рекомендации',
    'TRADE_SHOW': 'Выставка',
    'WEBFORM': 'CRM-форма',
    'CALLBACK': 'Обратный звонок',
    'RC_GENERATOR': 'Генератор продаж',
    'STORE': 'Интернет-магазин',
    'OTHER': 'Инстаграм',
    'FACE_TRACKER': 'Face-трекер',
    '1': 'Сенсус территории',
    'UC_UXN5EZ': 'Карта Сварщицы',
    'UC_NNQYKI': 'ВК',
    '1|AVITO': 'Avito - Коммуникация в соцсетях / открытые линии Битрикс24',
    'UC_8II8XP': 'Своя база (выгрузка по ИНН)',
    'UC_Z1BRAN': 'Выставка КЧК2025',
    '2': 'Аудит маркетинг Sample',
    '4': 'Аудит маркетинг Парьев',
    '3': 'Аудит маркетинг Вольный ростерс',
    '5': 'Источник не указан',
    'UC_I5A43K': 'Аудит маркетинг CoffeeGlobal',
    'UC_YRNROB': 'Источник Аудит Маркетинг Mr.Koff',
    'UC_MVPL32': 'Аудит маркетинг Дао кофе'
}

// Кэш для данных из схемы
let statusMappingCache = {
    statuses: null,
    sources: null
}

/**
 * Устанавливает данные маппинга из схемы коллекции
 *
 * @param {Object} collectionSchema - схема коллекции
 */
export const setStatusMappingFromSchema = (collectionSchema) => {
    if (!collectionSchema?.fields) return

    // Находим поля STATUS_ID и SOURCE_ID
    const statusField = collectionSchema.fields.find(f => f.name === 'STATUS_ID')
    const sourceField = collectionSchema.fields.find(f => f.name === 'SOURCE_ID')

    // Кэшируем данные статусов
    if (statusField?.possible_values) {
        statusMappingCache.statuses = {}
        statusField.possible_values.forEach(item => {
            statusMappingCache.statuses[item.id] = item.value
        })
        console.log('✅ Загружены статусы из схемы:', Object.keys(statusMappingCache.statuses).length)
    }

    // Кэшируем данные источников
    if (sourceField?.possible_values) {
        statusMappingCache.sources = {}
        sourceField.possible_values.forEach(item => {
            statusMappingCache.sources[item.id] = item.value
        })
        console.log('✅ Загружены источники из схемы:', Object.keys(statusMappingCache.sources).length)
    }
}

/**
 * Получает читаемое название статуса лида
 * Сначала ищет в данных схемы, потом в резервном маппинге
 *
 * @param {string} statusCode - Код статуса из Битрикса
 * @returns {string} - Читаемое название статуса
 */
export const getLeadStatusName = (statusCode) => {
    if (!statusCode) return 'Неизвестно'

    // Сначала пытаемся получить из кэша схемы
    if (statusMappingCache.statuses && statusMappingCache.statuses[statusCode]) {
        return statusMappingCache.statuses[statusCode]
    }

    // Резервный маппинг
    if (FALLBACK_LEAD_STATUS_MAPPING[statusCode]) {
        return FALLBACK_LEAD_STATUS_MAPPING[statusCode]
    }

    // Если ничего не найдено, возвращаем исходный код
    return statusCode
}

/**
 * Получает читаемое название источника лида
 * Сначала ищет в данных схемы, потом в резервном маппинге
 *
 * @param {string} sourceCode - Код источника из Битрикса
 * @returns {string} - Читаемое название источника
 */
export const getLeadSourceName = (sourceCode) => {
    if (!sourceCode) return 'Неизвестно'

    // Сначала пытаемся получить из кэша схемы
    if (statusMappingCache.sources && statusMappingCache.sources[sourceCode]) {
        return statusMappingCache.sources[sourceCode]
    }

    // Резервный маппинг
    if (FALLBACK_LEAD_SOURCE_MAPPING[sourceCode]) {
        return FALLBACK_LEAD_SOURCE_MAPPING[sourceCode]
    }

    // Если ничего не найдено, возвращаем исходный код
    return sourceCode
}

/**
 * Получает CSS класс для статуса лида (для цветовой индикации)
 *
 * @param {string} statusCode - Код статуса из Битрикса
 * @returns {string} - CSS класс
 */
export const getLeadStatusClass = (statusCode) => {
    // Маппинг статусов на CSS классы
    const statusClassMapping = {
        'NEW': 'text-blue-600 bg-blue-100',
        'IN_PROCESS': 'text-cyan-600 bg-cyan-100',
        'UC_LHZTAP': 'text-cyan-600 bg-cyan-100',
        'UC_8LUXZC': 'text-gray-600 bg-gray-100',
        '17': 'text-teal-600 bg-teal-100',
        '14': 'text-yellow-600 bg-yellow-100',
        '3': 'text-blue-600 bg-blue-100',
        'CONVERTED': 'text-green-600 bg-green-100',
        'JUNK': 'text-red-600 bg-red-100',
        '7': 'text-red-600 bg-red-100',
        '8': 'text-red-600 bg-red-100',
        '11': 'text-red-600 bg-red-100',
        '15': 'text-red-600 bg-red-100',
        '18': 'text-red-600 bg-red-100',
        '19': 'text-red-600 bg-red-100',
        'UC_0VBS2N': 'text-red-600 bg-red-100',
        'UC_5MM1GH': 'text-red-600 bg-red-100'
    }

    return statusClassMapping[statusCode] || 'text-gray-600 bg-gray-100'
}

/**
 * Получает все доступные статусы из кэша
 *
 * @returns {Object|null} - Объект со статусами или null
 */
export const getAllLeadStatuses = () => {
    return statusMappingCache.statuses || FALLBACK_LEAD_STATUS_MAPPING
}

/**
 * Получает все доступные источники из кэша
 *
 * @returns {Object|null} - Объект с источниками или null
 */
export const getAllLeadSources = () => {
    return statusMappingCache.sources || FALLBACK_LEAD_SOURCE_MAPPING
}

/**
 * Очищает кэш маппинга (используется при смене коллекции)
 */
export const clearStatusMappingCache = () => {
    statusMappingCache.statuses = null
    statusMappingCache.sources = null
    console.log('🗑️ Кэш маппинга статусов очищен')
}

/**
 * Преобразует данные агрегации статусов лидов для диаграмм
 * Заменяет коды статусов на читаемые названия
 *
 * @param {Array} aggregationData - данные агрегации [{_id: statusCode, count: number}]
 * @returns {Array} - преобразованные данные [{_id: statusName, originalCode: statusCode, count: number}]
 */
export const transformLeadStatusData = (aggregationData) => {
    if (!Array.isArray(aggregationData)) {
        console.warn('transformLeadStatusData: Данные должны быть массивом')
        return []
    }

    return aggregationData.map(item => {
        if (!item || typeof item !== 'object') {
            console.warn('transformLeadStatusData: Некорректный элемент данных:', item)
            return {
                _id: 'Неизвестно',
                originalCode: null,
                count: 0
            }
        }

        const statusCode = item._id
        const statusName = getLeadStatusName(statusCode)
        const count = typeof item.count === 'number' ? item.count : 0

        return {
            _id: statusName,           // Читаемое название для диаграммы
            originalCode: statusCode,  // Сохраняем оригинальный код
            count: count
        }
    }).filter(item => item.count > 0) // Фильтруем пустые статусы
}